<!-- Header -->
<div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
	<div>
		<h1 class="text-xl md:text-3xl font-cenzo font-bold text-primary">Blog Management</h1>
		<p class="text-charcoal/60 mt-1 md:mt-2 text-sm md:text-base">Create and manage your blog posts</p>
	</div>
	<button
		(click)="addNewBlog()"
		class="inline-flex items-center justify-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg md:rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 text-sm md:text-base"
	>
		<svg class="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
		</svg>
		<span class="hidden sm:inline">Create New Post</span>
		<span class="sm:hidden">New Post</span>
	</button>
</div>

<!-- Main Content -->
<div>
	@if (isLoading()) {
		<!-- Loading State -->
		<div class="flex items-center justify-center py-20">
			<div class="text-center">
				<div
					class="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"
				></div>
				<p class="text-charcoal/60 mt-4">Loading blogs...</p>
			</div>
		</div>
	} @else if (errorMessage()) {
		<!-- Error State -->
		<div
			class="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center gap-3"
		>
			<svg class="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
				<path
					fill-rule="evenodd"
					d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
					clip-rule="evenodd"
				/>
			</svg>
			<span>{{ errorMessage() }}</span>
		</div>
	} @else if (blogs().length === 0) {
		<!-- Empty State -->
		<div class="text-center py-20">
			<div
				class="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6"
			>
				<svg class="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
					/>
				</svg>
			</div>
			<h3 class="text-2xl font-cenzo font-bold text-primary mb-2">No Posts Yet</h3>
			<p class="text-charcoal/60 mb-6">Get started by creating your first blog post</p>
			<button
				(click)="addNewBlog()"
				class="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
			>
				<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 4v16m8-8H4"
					/>
				</svg>
				Create First Post
			</button>
		</div>
	} @else {
		<!-- Blog Cards Grid -->
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
			@for (blog of blogs(); track blog.id) {
			<div
				class="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow border border-charcoal/5 group"
			>
				<!-- Blog Image -->
				<div class="relative h-40 sm:h-48 md:h-56 overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10">
					<img
						[src]="getImageUrl(blog.featured_image_url)"
						[alt]="blog.title"
						class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
						(error)="onImageError($event)"
					/>
					<!-- Status Badges -->
					<div class="absolute top-4 left-4 flex gap-2">
						@if (blog.is_published) {
						<span
							class="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/90 text-white backdrop-blur-sm flex items-center gap-1"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
									clip-rule="evenodd"
								/>
							</svg>
							Published
						</span>
						} @else {
						<span
							class="px-3 py-1 rounded-full text-xs font-semibold bg-gray-500/90 text-white backdrop-blur-sm flex items-center gap-1"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
									clip-rule="evenodd"
								/>
							</svg>
							Draft
						</span>
						}
						@if (blog.is_featured) {
						<span
							class="px-3 py-1 rounded-full text-xs font-semibold bg-secondary/90 text-white backdrop-blur-sm flex items-center gap-1"
						>
							<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
							</svg>
							Featured
						</span>
						}
					</div>
				</div>

				<div class="p-4 md:p-6">
					<!-- Title -->
					<h3 class="text-base md:text-xl font-cenzo font-bold text-primary mb-2 line-clamp-2 group-hover:text-secondary transition-colors">
						{{ blog.title }}
					</h3>

					<!-- Short Description -->
					<p class="text-charcoal/70 text-xs md:text-sm mb-3 md:mb-4 line-clamp-2">
						{{ blog.short_description }}
					</p>

					<!-- Meta Info -->
					<div class="flex flex-wrap items-center gap-2 md:gap-3 text-xs text-charcoal/50 mb-3 md:mb-4 pb-3 md:pb-4 border-b border-charcoal/10">
						<div class="flex items-center gap-1">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
							</svg>
							<span>{{ formatDate(blog.created_at) }}</span>
						</div>
						<div class="flex items-center gap-1">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
							</svg>
							<span>{{ blog.views_count }} views</span>
						</div>
						<div class="flex items-center gap-1">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
							</svg>
							<span>{{ blog.likes_count }} likes</span>
						</div>
					</div>

					<!-- Action Buttons -->
					<div class="flex gap-1.5 md:gap-2">
						<button
							(click)="editBlog(blog.id)"
							class="flex-1 px-3 py-1.5 md:px-4 md:py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-xs md:text-sm font-semibold transition-all flex items-center justify-center gap-1.5 md:gap-2"
						>
							<svg class="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
							</svg>
							Edit
						</button>
						<button
							(click)="togglePublish(blog)"
							[class.bg-green-500]="!blog.is_published"
							[class.hover:bg-green-600]="!blog.is_published"
							[class.bg-gray-500]="blog.is_published"
							[class.hover:bg-gray-600]="blog.is_published"
							class="px-2.5 py-1.5 md:px-4 md:py-2 text-white rounded-lg text-xs md:text-sm font-semibold transition-all"
							[title]="blog.is_published ? 'Unpublish' : 'Publish'"
						>
							@if (blog.is_published) {
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
							</svg>
							} @else {
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
							</svg>
							}
						</button>
						<button
							(click)="deleteBlog(blog)"
							class="px-2.5 py-1.5 md:px-4 md:py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs md:text-sm font-semibold transition-all"
							title="Delete"
						>
							<svg class="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
							</svg>
						</button>
					</div>
				</div>
			</div>
			}
		</div>
	}
</div>
